import { useState, useEffect } from "react";
import { ColorPickerButton, getCurrentTextColor } from "./commands/PickerColor";
import SvgImage from "components/SvgImage/SvgImage";

const Toolbar = ({ editorRef, toolbar = [] }) => {
  const [activeFormats, setActiveFormats] = useState(new Set());
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false });
  const [currentTextColor, setCurrentTextColor] = useState('#000000');

  const updateHistoryState = () => {
    const canUndo = (editorRef?.current?.history?.undoStack?.length || 0) > 1;
    const canRedo = (editorRef?.current?.history?.redoStack?.length || 0) > 0;
    setHistoryState({ canUndo, canRedo });
  };

  const exec = (name, payload) => {
    editorRef.current?.commands?.execute(name, payload);
    updateHistoryState();

    // Update color immediately after executing color command
    if (name === 'foreColor') {
      setTimeout(() => {
        updateActiveFormats();
      }, 50);
    }
  };

  const updateActiveFormats = () => {
    // Check if the current editor is focused/active
    const editorRoot = editorRef?.current?.root;
    const selection = window.getSelection();

    // Only update if selection is within this editor
    if (!editorRoot || !selection || selection.rangeCount === 0) {
      setActiveFormats(new Set());
      setCurrentTextColor('#000000');
      return;
    }

    const range = selection.getRangeAt(0);
    const isSelectionInEditor = editorRoot.contains(range.commonAncestorContainer);

    if (!isSelectionInEditor) {
      // Selection is not in this editor, clear active formats
      setActiveFormats(new Set());
      setCurrentTextColor('#000000');
      return;
    }

    const formats = new Set();

    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('strikeThrough')) formats.add('strike');

    setActiveFormats(formats);

    // Update current text color
    const color = getCurrentTextColor();

    setCurrentTextColor(color);
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      updateActiveFormats();
      updateHistoryState();
    };

    const handleHistoryChange = () => {
      updateHistoryState();
    };

    const handleInput = () => {
      // Update active formats on input to reflect changes immediately
      updateActiveFormats();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    window.addEventListener('editor-history-change', handleHistoryChange);

    // Listen to input events on the editor
    const editorRoot = editorRef?.current?.root;
    if (editorRoot) {
      editorRoot.addEventListener('input', handleInput);
    }

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      window.removeEventListener('editor-history-change', handleHistoryChange);
      if (editorRoot) {
        editorRoot.removeEventListener('input', handleInput);
      }
    };
  }, [editorRef]);

  const handleMouseDown = (e, command) => {
    e.preventDefault(); // giữ selection

    if (command === 'link') {
      const blocks = editorRef.current.selection.getSelectedBlocks();
      const isAddLink = Array.from(blocks).every((block) => block.tagName.toLowerCase() !== 'li');
      if (isAddLink) {
        const url = prompt('Enter URL');
        if (url) exec(command, url);
      }
    } else {
      exec(command);
    }
  };

  const getButtonClass = (format) => {
    const base =
      'w-12 h-9 border flex items-center justify-center hover:bg-gray-100 transition-colors hover:cursor-pointer';
    const active = format && activeFormats.has(format) ? '' : '';
    return `${base} ${active}`;
  };

  return (
    <div className="flex items-center gap-4 bg-white">
      {toolbar.map((group, groupIndex) => (
        <div key={groupIndex} className="flex">
          {group.map((tool, index) => {
            // 🎨 Color Picker
            if (tool.command === 'foreColor' && tool.src) {
              return (
                <div key={index} className="relative">
                  <ColorPickerButton
                    icon={tool.src}
                    currentTextColor={currentTextColor}
                    onColorChange={(color) => exec('foreColor', color)}
                  />
                </div>
              );
            }

            if (tool.command === 'undo') {
              return (
                <button
                  key={index}
                  onMouseDown={(e) => handleMouseDown(e, tool.command)}
                  className={getButtonClass(tool.format)}
                  disabled={!historyState.canUndo}
                >
                  {tool.src && (
                    <SvgImage
                      src={tool.src}
                      width={24}
                      height={24}
                      className={historyState.canUndo ? 'opacity-60' : 'opacity-30'}
                    />
                  )}
                </button>
              );
            }

            if (tool.command === 'redo') {
              return (
                <button
                  key={index}
                  onMouseDown={(e) => handleMouseDown(e, tool.command)}
                  className={getButtonClass(tool.format)}
                  disabled={!historyState.canRedo}
                >
                  {tool.src && (
                    <SvgImage
                      src={tool.src}
                      width={24}
                      height={24}
                      className={historyState.canRedo ? 'opacity-60' : 'opacity-30'}
                    />
                  )}
                </button>
              );
            }

            return (
              <button
                key={index}
                onMouseDown={(e) => handleMouseDown(e, tool.command)}
                className={getButtonClass(tool.command)}
              >
                {tool.src && (
                  <SvgImage
                    src={tool.src}
                    width={24}
                    height={24}
                    className={activeFormats.has(tool.command) ? 'opacity-70' : 'opacity-30'}
                  />
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default Toolbar;